import { PageAbout, Story, StoryListing, Work, WorkImage } from '@/types';
import { reactive, App, InjectionKey, inject } from 'vue';
export const storeKey: InjectionKey<Store> = Symbol('store');

export interface StoreState {
  meta: {
    title: string;
    defaultTitle: string;
    description: string;
    defaultDescription: string;
  };
  backgroundWhite: boolean;
  navLight: boolean;
  navActive: boolean;
  homeStories: string[];
  archiveStories: string[];
  stories: {
    [key: string]: StoryListing | Story;
  };
  works: {
    [key: string]: Work<WorkImage>;
  };
  pages: {
    about: PageAbout | undefined;
  };
  form: {
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
}

export interface StoreMutations {
  setBackgroundWhite: (isWhite?: boolean) => void;
  setNavActive: (active: boolean) => void;
  toggleNav: () => void;
  setNavLight: (light: boolean) => void;
  setStory: (story: Story | StoryListing) => void;
  setHomeStories: (stories: string[]) => void;
  setArchiveStories: (stories: string[]) => void;
  setPage: <T extends keyof StoreState['pages']>(
    key: T,
    content: StoreState['pages'][T]
  ) => void;
  setWork: (work: Work) => void;
  setFormValue: (key: keyof StoreState['form'], value: string) => void;
  setMetaTitle: (title?: string, defaultTitle?: string) => void;
  setMetaDescription: (
    description?: string,
    defaultDescription?: string
  ) => void;
}

export interface Store {
  state: StoreState;
  commit: StoreMutations;
}

function createStore() {
  const state: StoreState = reactive({
    meta: {
      title: 'Emalin Stories',
      defaultTitle: 'Emalin Stories',
      description: '',
      defaultDescription: '',
    },
    backgroundWhite: false,
    navActive: false,
    navLight: false,
    pages: {
      about: undefined,
    },
    stories: {},
    works: {},
    homeStories: [],
    archiveStories: [],
    form: {
      name: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
  });

  const commit: StoreMutations = {
    setBackgroundWhite(isWhite = true) {
      state.backgroundWhite = isWhite;
      if (isWhite) {
        document.body.classList.add('bg-white');
      } else {
        document.body.classList.remove('bg-white');
      }
    },
    setMetaTitle(title, defaultTitle) {
      if (defaultTitle) {
        state.meta.defaultTitle = defaultTitle;
      }
      state.meta.title = title
        ? `${title} | ${state.meta.defaultTitle}`
        : state.meta.defaultTitle;
    },
    setMetaDescription(description, defaultDescription) {
      if (defaultDescription) {
        state.meta.defaultDescription = defaultDescription;
      }
      state.meta.description = description || state.meta.defaultDescription;
    },
    setNavActive(active = true) {
      state.navActive = active;
    },
    toggleNav() {
      state.navActive = !state.navActive;
    },
    setNavLight(light) {
      state.navLight = light === true;
    },
    setHomeStories(stories) {
      state.homeStories = stories;
    },
    setArchiveStories(stories) {
      state.archiveStories = stories;
    },
    setStory(story) {
      state.stories[story.slug] = story;
    },
    setWork(work) {
      state.works[work.slug] = work;
    },
    setPage(key, content) {
      state.pages[key] = content;
    },
    setFormValue(key, value) {
      state.form[key] = value;
    },
  };

  const store = {
    state,
    commit,
    install(app: App) {
      app.provide(storeKey, store);
      return store;
    },
  };

  return store;
}

const store = createStore();

export default store;

export function useStore(): Store {
  return inject(storeKey)!;
}

export function setMeta(
  meta: { title?: string; description?: string; metaDescription?: string } = {}
) {
  store.commit.setMetaTitle(meta.title);
  store.commit.setMetaDescription(meta.description || meta.metaDescription);
}
